import { Button, Modal, ModalClose } from "@govlaunch/core";
import React, { ReactNode, useState } from "react";
import { FormattedMessage } from "react-intl";
import useIntlDate from "~/lib/hooks/useIntlDate";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import Strongify from "~/lib/utils/strongify";
import { IPlanRecurrency } from "~/types/types";

type TStripePlan = NonNullable<NonNullable<ICompanyAccountBillingInfoQuery["company"]>["stripePlan"]>;

interface IConfirmBillingRecurrencySwitchChildrenState {
  onConfirmBeforeSwitching: () => void;
}

interface IConfirmBillingRecurrencySwitchProps {
  fromPlan: TStripePlan;
  toPlan: TStripePlan;
  onConfirm: () => void;
  children: (state: IConfirmBillingRecurrencySwitchChildrenState) => ReactNode;
}

export default function ConfirmBillingRecurrencySwitch({
  fromPlan,
  toPlan,
  children,
  onConfirm,
}: IConfirmBillingRecurrencySwitchProps) {
  const intlDate = useIntlDate();
  const fromRecurrency = fromPlan.recurrency === IPlanRecurrency.Monthly ? "monthly" : "yearly";
  const toRecurrency = toPlan.recurrency === IPlanRecurrency.Monthly ? "monthly" : "yearly";

  const [isOpen, setIsOpen] = useState(false);

  const onRequestClose = () => setIsOpen(false);

  return (
    <>
      <Modal isOpen={isOpen} ariaHideApp={false} onRequestClose={onRequestClose}>
        <div
          css={{
            display: "grid",
            gridRowGap: 20,
            width: 370,
            padding: 20,
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <h2
              css={{
                fontWeight: "bold",
                margin: 0,
              }}
            >
              <FormattedMessage defaultMessage="Confirm Switch" id="jwXDYh" />
            </h2>

            <ModalClose
              onClick={onRequestClose}
              style={{
                position: "relative",
                top: 0,
                right: 0,
                marginLeft: "auto",
                cursor: "pointer",
              }}
            />
          </div>

          <p
            css={{
              margin: 0,
              fontWeight: 400,
              fontSize: 14,
              lineHeight: 1.33,
            }}
          >
            <FormattedMessage
              defaultMessage="You're about to switch your payment frequency from <strong>{fromRecurrency}</strong> to <strong>{toRecurrency}</strong>."
              id="D3K8rN"
              values={{
                fromRecurrency: fromRecurrency,
                toRecurrency: toRecurrency,
                strong: (chunk: any) => Strongify(chunk),
              }}
            />
          </p>

          {toPlan.changeDetails.prorateCost && toPlan.changeDetails.prorateCost > 0 ? (
            <p
              css={{
                margin: 0,
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.33,
              }}
            >
              <FormattedMessage
                defaultMessage="If you proceed, we'll prorate charge of <strong>{prorateCharge}</strong> now and your next payment due will be <strong>{nextPaymentDue}</strong>."
                id="OGcCCo"
                values={{
                  prorateCharge: toPlan.changeDetails.prorateCost < 100 ? 0 : toPlan.changeDetails.prorateCost / 100,
                  nextPaymentDue: intlDate.format(toPlan.changeDetails.firstInvoiceAfterProrateDate),
                  strong: (chunk: any) => Strongify(chunk),
                }}
              />
            </p>
          ) : (
            <p
              css={{
                margin: 0,
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.33,
              }}
            >
              <FormattedMessage
                defaultMessage="By clicking proceed, we'll change the frequency without any charges. Your next payment will be <strong>{nextPaymentDue}</strong>."
                id="fiTVxH"
                values={{
                  nextPaymentDue: intlDate.format(toPlan.changeDetails.proratePaymentDueDate),
                  strong: (chunk: any) => Strongify(chunk),
                }}
              />
            </p>
          )}

          <div
            css={{
              display: "grid",
              gridColumnGap: 20,
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <Button theme="link" color="danger" onClick={onRequestClose}>
              <FormattedMessage defaultMessage="No. Don't Switch" id="qqc0df" />
            </Button>

            <Button
              onClick={() => {
                onConfirm();
                onRequestClose();
              }}
            >
              <FormattedMessage defaultMessage="Proceed" id="VNX4fn" />
            </Button>
          </div>
        </div>
      </Modal>

      {children({
        onConfirmBeforeSwitching: () => setIsOpen(true),
      })}
    </>
  );
}
